import React, { useState } from 'react';

const QCodesQuiz = ({ quiz }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [answeredQuestions, setAnsweredQuestions] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);

  if (!quiz || !quiz.questions) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 sm:p-8 text-center">
        <p className="text-gray-600 text-sm sm:text-base">No quiz available.</p>
      </div>
    );
  }

  const handleAnswerSelect = (questionId, answerIndex) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [questionId]: answerIndex
    });
    
    setAnsweredQuestions({
      ...answeredQuestions,
      [questionId]: true
    });
  };

  const handleNext = () => {
    if (currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResults(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const calculateScore = () => {
    let correct = 0;
    quiz.questions.forEach(question => {
      if (selectedAnswers[question.id] === question.correct) {
        correct++;
      }
    });
    return correct;
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setAnsweredQuestions({});
    setShowResults(false);
    setQuizStarted(false);
  };

  if (!quizStarted) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 sm:p-8 text-center">
        <div className="text-4xl mb-4">📻</div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
          {quiz.title}
        </h2>
        <p className="text-gray-600 mb-6 text-sm sm:text-base">
          Test your knowledge of essential Q codes with {quiz.questions.length} questions
        </p>
        <p className="text-gray-500 mb-6 text-sm">
          Learn the standard abbreviation codes used in amateur radio communication
        </p>
        <button
          onClick={() => setQuizStarted(true)}
          className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors w-full sm:w-auto text-base"
        >
          Start Q Codes Quiz
        </button>
      </div>
    );
  }

  if (showResults) {
    const score = calculateScore();
    const percentage = Math.round((score / quiz.questions.length) * 100);
    
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 sm:p-8">
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
            Q Codes Quiz Results
          </h2>
          <div className="text-4xl sm:text-6xl mb-4">
            {percentage >= 80 ? '🎉' : percentage >= 60 ? '👍' : '📚'}
          </div>
          <p className="text-lg sm:text-xl text-gray-700 mb-2">
            You scored {score} out of {quiz.questions.length}
          </p>
          <p className="text-base sm:text-lg text-gray-600 mb-6">
            ({percentage}%)
          </p>
          
          {percentage >= 80 && (
            <p className="text-green-600 font-semibold">Excellent! You've mastered the Q codes!</p>
          )}
          {percentage >= 60 && percentage < 80 && (
            <p className="text-yellow-600 font-semibold">Good job! Review a few more Q codes and try again.</p>
          )}
          {percentage < 60 && (
            <p className="text-red-600 font-semibold">Study the Q codes more and try again. Practice makes perfect!</p>
          )}
        </div>

        <div className="space-y-4 sm:space-y-6">
          {quiz.questions.map((question, index) => {
            const userAnswer = selectedAnswers[question.id];
            const isCorrect = userAnswer === question.correct;
            
            return (
              <div key={question.id} className="border border-gray-200 rounded-lg p-4 sm:p-6">
                <h3 className="font-semibold text-gray-900 mb-3 text-sm sm:text-base">
                  Question {index + 1}: {question.question}
                </h3>
                
                <div className="space-y-2 mb-4">
                  {question.options.map((option, optionIndex) => (
                    <div 
                      key={optionIndex}
                      className={`p-3 rounded-md text-sm ${
                        optionIndex === question.correct
                          ? 'bg-green-100 border border-green-300 text-green-800'
                          : optionIndex === userAnswer && !isCorrect
                          ? 'bg-red-100 border border-red-300 text-red-800'
                          : 'bg-gray-50'
                      }`}
                    >
                      <div className="break-words">
                        {option}
                        {optionIndex === question.correct && ' ✓ (Correct)'}
                        {optionIndex === userAnswer && !isCorrect && ' ✗ (Your answer)'}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="text-blue-800 text-sm">
                    <strong>Explanation:</strong> <span className="break-words">{question.explanation}</span>
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="mt-6 sm:mt-8 text-center">
          <button
            onClick={resetQuiz}
            className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors w-full sm:w-auto text-base"
          >
            Retake Quiz
          </button>
        </div>
      </div>
    );
  }

  const question = quiz.questions[currentQuestion];
  const selectedAnswer = selectedAnswers[question.id];
  const isAnswered = answeredQuestions[question.id];
  const isCorrect = isAnswered && selectedAnswer === question.correct;

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-8">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 space-y-2 sm:space-y-0">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
            Q Codes Quiz - Question {currentQuestion + 1} of {quiz.questions.length}
          </h2>
          <div className="text-sm text-gray-500">
            Progress: {Math.round(((currentQuestion + 1) / quiz.questions.length) * 100)}%
          </div>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentQuestion + 1) / quiz.questions.length) * 100}%` }}
          />
        </div>
      </div>

      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-6 break-words">
        {question.question}
      </h3>

      <div className="space-y-3 mb-6">
        {question.options.map((option, index) => {
          let optionClass = 'block p-4 sm:p-4 border rounded-lg transition-colors touch-manipulation ';
          
          if (isAnswered) {
            if (index === question.correct) {
              optionClass += 'border-green-500 bg-green-100 text-green-800 cursor-default';
            } else if (index === selectedAnswer && selectedAnswer !== question.correct) {
              optionClass += 'border-red-500 bg-red-100 text-red-800 cursor-default';
            } else {
              optionClass += 'border-gray-200 bg-gray-50 text-gray-600 cursor-default';
            }
          } else {
            optionClass += selectedAnswer === index
              ? 'border-blue-500 bg-blue-50 cursor-pointer active:bg-blue-100'
              : 'border-gray-200 hover:border-gray-300 active:bg-gray-100 cursor-pointer';
          }
          
          return (
            <label 
              key={index}
              className={optionClass}
            >
              <input
                type="radio"
                name={`question-${question.id}`}
                value={index}
                checked={selectedAnswer === index}
                onChange={() => !isAnswered && handleAnswerSelect(question.id, index)}
                className="sr-only"
                disabled={isAnswered}
              />
              <div className="flex items-start sm:items-center justify-between space-x-3">
                <span className="text-gray-700 text-sm sm:text-base break-words flex-1">{option}</span>
                {isAnswered && index === question.correct && (
                  <span className="text-green-600 font-semibold text-sm sm:text-base flex-shrink-0">✓ Correct</span>
                )}
                {isAnswered && index === selectedAnswer && selectedAnswer !== question.correct && (
                  <span className="text-red-600 font-semibold text-sm sm:text-base flex-shrink-0">✗ Your answer</span>
                )}
              </div>
            </label>
          );
        })}
      </div>

      {isAnswered && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <div className="text-xl sm:text-2xl flex-shrink-0">
              {isCorrect ? '📻' : '📚'}
            </div>
            <div className="flex-1">
              <p className="font-semibold text-blue-900 mb-2 text-sm sm:text-base">
                {isCorrect ? 'Correct! Well done!' : 'Not quite right.'}
              </p>
              <p className="text-blue-800 text-sm break-words">
                <strong>Explanation:</strong> {question.explanation}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between space-y-3 sm:space-y-0 sm:space-x-4">
        <button
          onClick={handlePrevious}
          disabled={currentQuestion === 0}
          className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base touch-manipulation"
        >
          Previous
        </button>

        <button
          onClick={handleNext}
          disabled={!isAnswered}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base touch-manipulation"
        >
          {currentQuestion === quiz.questions.length - 1 ? 'Finish Quiz' : 'Next'}
        </button>
      </div>
    </div>
  );
};

export default QCodesQuiz;